/**
 * launchPayload — the ONE producer seam for the per-email identity half of a
 * workflow launch payload, shared by every host.
 *
 * An email has exactly one name in this system: its RFC 5322 Message-ID
 * (`internet_message_id`). It is a DECLARED input of the launch contract —
 * the substrate's concurrent-run guard, the extraction cache and ambient
 * intake all key on it — so it rides `inputs`, never `metadata`. A metadata
 * key is exactly the free-form shape the contract drops.
 *
 * `graph_message_id` is NOT the identity. It is the mailbox-scoped Outlook
 * item id the add-in's reply-draft path calls Graph with; it differs per
 * mailbox and per move. It keeps riding under its existing key, unchanged,
 * and stays `undefined` for a host (ora) that has no such id to give it.
 *
 * Every launch builds its identity fields HERE — a second inline copy of the
 * rule is how producers drift apart (the exact failure AC-3 exists to
 * prevent). Moved from add-in-only `src/launch/launchPayload.ts` to this
 * shared location (story 26752) when ora's `workflows.triage.$messageId`
 * route became the second producer. The substrate's mirror of this ownership
 * is `workflows/tools/message_key.py`.
 */

/**
 * The refusal an identity-less launch earns. Deliberately the SAME sentence
 * the substrate raises (`message_key.MISSING_IDENTITY_REFUSAL`) so the
 * operator reads one instruction whether the client or the server caught it.
 */
export const MISSING_MESSAGE_IDENTITY_MESSAGE =
  "internet_message_id is required — this launch carries no per-email identity. "
  + "Reload the task pane and try again.";

/** Thrown when a launch that REQUIRES the per-email identity has none. */
export class MissingMessageIdentityError extends Error {
  constructor(message: string = MISSING_MESSAGE_IDENTITY_MESSAGE) {
    super(message);
    this.name = "MissingMessageIdentityError";
  }
}

/** The email-id surface a launch payload needs (structural). */
export interface LaunchIdentitySource {
  /** RFC 5322 Message-ID — the per-email identity. */
  internetMessageId?: string | null;
  /** Office item id — the Graph handle, NOT the identity. */
  graphMessageId?: string | null;
}

export interface BuildLaunchIdentityOptions {
  /**
   * Whether the target workflow DECLARES `internet_message_id` as a required
   * input. Defaults to `true` — the strict contract.
   *
   * Resolve it from the workflow's published schema with
   * `launchRequiresMessageIdentity`, never by hard-coding a workflow id: only
   * `carrier_mortgagee_update_v2` declares the identity today, while the live
   * fleet (`mortgagee_update_v1_2`, `epic_bulk_policy_update_v1`) does not and
   * must keep launching from Outlook hosts that surface an empty
   * `item.internetMessageId`.
   */
  requireIdentity?: boolean;
}

/**
 * Build the identity half of a launch payload's `inputs`.
 *
 * Throws `MissingMessageIdentityError` when the workflow requires the identity
 * and the source carries none — the substrate refuses that launch (AC-7), so
 * failing here with the same instruction beats sending a payload we already
 * know will be rejected.
 */
export function buildLaunchIdentityInputs(
  src: LaunchIdentitySource,
  opts: BuildLaunchIdentityOptions = {},
): Record<string, string> {
  const requireIdentity = opts.requireIdentity ?? true;
  const identity = (src.internetMessageId ?? "").trim();
  if (!identity && requireIdentity) {
    throw new MissingMessageIdentityError();
  }
  return {
    ...(identity ? { internet_message_id: identity } : {}),
    // Unchanged from the drawer's long-standing silent capture: sent as-is
    // whenever the host gave us one, under its existing key.
    ...(src.graphMessageId ? { graph_message_id: src.graphMessageId } : {}),
  };
}

/**
 * Does this workflow declare the per-email identity as a required input?
 *
 * Read from the workflow's own published `input_schema` (the substrate is the
 * source of truth for its contract) rather than from a client-side list of
 * workflow ids, so a workflow that gains or loses the field needs no add-in
 * change.
 */
export function launchRequiresMessageIdentity(inputSchema: unknown): boolean {
  if (!inputSchema || typeof inputSchema !== "object") return false;
  const required = (inputSchema as { required?: unknown }).required;
  return Array.isArray(required) && required.includes("internet_message_id");
}

export default buildLaunchIdentityInputs;
